import { Component, OnInit } from '@angular/core';
import firebase from 'firebase';
import { FirebaseService } from '../../../firebase/firebase.service';
import { Router } from '@angular/router';
import { AppContextService } from '../../../shared/services/app-context.service';
import { waitForTick } from '../../../shared/wait-for-tick';
import { NgForm } from '@angular/forms';
import { touchForm } from '../../../shared/touch-form';
import { interval } from 'rxjs';
import { finalize, take, tap } from 'rxjs/operators';
import { NgFor } from '@angular/common';

const TIMER_COUNT = 60;

@Component({
  selector: 'townip-mfa-setup',
  templateUrl: './mfa-setup.component.html',
  styleUrl: './mfa-setup.component.scss'
})
export class MfaSetupComponent implements OnInit{

  public mfaActive = true;

  public mfaPhone: string;

  public mfaCodeSent = false;

  public mfaVerificationCode: string;

  public mfaVerificationId: string;

  public reCaptchaVerifier: firebase.auth.RecaptchaVerifier;

  public reCaptchaVerifierForResend: firebase.auth.RecaptchaVerifier;

  public mfaError = false;

  public mfaErrorMsg = '';

  public resendDisabled = false;

  public timerCount = 0;

  public showResetSuccess = true;

  constructor(
    public appContext: AppContextService,
    private firebaseService: FirebaseService,
    private router: Router,
  ) { }

  public ngOnInit(): void {
    setTimeout(() => {
      this.reCaptchaVerifier = this.firebaseService
        .initInvisibleCaptchaVerifier('btn-verify-phone');
    }, 1500);
  }

  public async sendPhoneNumberVerification(form: NgForm): Promise<void> {
    touchForm(form);
    if (form.invalid) {
      return;
    }

    this.resetErrors();
    this.showResetSuccess = false;

    try {
      this.mfaVerificationId = await this.firebaseService
        .sendMfaCodeForEnrollment(this.reCaptchaVerifier, this.mfaPhone);
      this.mfaCodeSent = true;
      this.setupResendRecaptcha();
    } catch (e) {
      this.mfaCodeSent = false;
      this.mfaError = true;
      this.mfaErrorMsg = this.firebaseService.translateErrorCode(e.code, e.message);

      console.error(e);
    }
  }

  public async verifyCode(form: NgForm): Promise<void> {
    if (!this.mfaActive) {
      await this.router.navigate(['/auth/logout']);
      return;
    }

    touchForm(form);
    if (form.invalid) {
      return;
    }

    this.resetErrors();

    try {
      await this.firebaseService.verifyMfaEnroll(this.mfaVerificationId, this.mfaVerificationCode);
      this.router.navigate(['/auth/mfa-setup-complete']);
    } catch (e) {
      this.mfaError = true;
      this.mfaErrorMsg = 'Failed to verify code.';

      if (e.code === 'auth/invalid-verification-code') {
        this.mfaErrorMsg = 'That code is not valid. Resend code if needed.';
      }

      if (e.code === 'auth/code-expired') {
        this.mfaErrorMsg = 'Code Expired. Resend code if needed.';
      }

      console.error(e);
    }
  }

  public async resendCode(btn: HTMLButtonElement): Promise<void> {
    if (this.resendDisabled) {
      return;
    }

    this.resetErrors();
    this.startResendTimer();

    try {
      this.mfaVerificationId = await this.firebaseService
        .sendMfaCodeForEnrollment(this.reCaptchaVerifierForResend, this.mfaPhone);
      btn.disabled = true;
    } catch (e) {
      this.mfaError = true;
      this.firebaseService.translateErrorCode(e.code, e.message);
      console.error(e);
    }
  }

  public changePhoneNumber(): void {
    this.resetErrors();
    this.mfaCodeSent = false;
    this.mfaVerificationId = '';
    this.mfaVerificationCode = '';

    if (this.reCaptchaVerifierForResend) {
      this.reCaptchaVerifierForResend.clear();
    }
  }

  public cancel(): void {
    this.router.navigate(['/auth/logout']);
  }

  private resetErrors(): void {
    this.mfaError = false;
    this.mfaErrorMsg = '';
  }

  private async setupResendRecaptcha(): Promise<void> {
    await waitForTick();
    this.reCaptchaVerifierForResend = this.firebaseService
      .initInvisibleCaptchaVerifier('btn-resend');
  }

  private startResendTimer(): void {
    this.resendDisabled = true;
    this.timerCount = TIMER_COUNT;

    // No need to cleanup, the timer will always clear after the TIMER_COUNT.
    interval(1000)
      .pipe(
        tap(() => {
          this.timerCount --;
        }),
        take(TIMER_COUNT),
        finalize(() => {
          this.resendDisabled = false;
        })
      )
      .subscribe();
  }
}
